// Capabilities — tool grid on its own route.
import { Search, Activity, Shield, FlaskConical, BarChart3, Target, Brain, MessageSquare } from "lucide-react";
import { C, SectionLabel, PublicPage } from "@/components/PublicShell";
import { DashboardShowcase } from "@/components/DashboardShowcase";

const CAPS = [
  { icon: Search, title: "Scanner", body: "Regime classification across your full watchlist. Four states: Quiet, Bull, Bear, Vol. Strategy ranking per ticker, updated in real time, based on objective regime-fit criteria." },
  { icon: Activity, title: "TickerWorkbench", body: "Deep single-ticker analysis. Strategy ranking by regime fit, backtest win rate, and skew. DTE timeline with earnings and expiry markers. Pre-market gap stress before the open." },
  { icon: Brain, title: "Persistent Regimes", body: "A 4-state Hidden Markov Model trained per ticker, backfilled daily, and overlaid as coloured bands on every price chart. Sojourn, transition risk, and entropy exposed alongside the label." },
  { icon: Target, title: "Wheel Screener", body: "Screen cash-secured puts, covered calls, and spreads with strikes, deltas, yields, and P(non-touch) side by side — choose between regime-scaled delta, key-level, and PNT strike-selection engines." },
  { icon: FlaskConical, title: "Reliability Lab", body: "Position lifecycle monitoring. PARS health score, regime drift detection, and status classification \u2014 Stable, Watch, High Risk \u2014 for every open trade. Roll alerts included." },
  { icon: Shield, title: "Drawdown Analytics", body: "Portfolio-level drawdown analytics. Define your NLV floor. See max drawdown exposure by position. Regime-aware protective candidates identified by objective, rules-based criteria that adapt to current market conditions." },
  { icon: BarChart3, title: "Backtest Lab", body: "Historical backtesting for every strategy. Review win rate, average return, and max drawdown under similar historical regime conditions. Backtested results are hypothetical and do not guarantee future performance." },
  { icon: MessageSquare, title: "Portfolio Assistant (beta)", body: "Natural-language readouts of your own analytics. Ask about your portfolio's computed risk, screens, allocations, quotes, and chain figures — every number comes from the platform's engines and your own Settings. It reads out; it never recommends." },
];

export default function CapabilitiesPage() {
  return (
    <PublicPage>
      <section className="py-16 px-5" data-testid="capabilities-page">
        <div className="max-w-5xl mx-auto">
          <SectionLabel text="Capabilities" color={C.muted} />
          <h1 className="text-xl sm:text-2xl font-extrabold tracking-tight mb-10" style={{ color: C.text }}>
            Every tool you need. Nothing you don&apos;t.
          </h1>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {CAPS.map(({ icon: Icon, title, body }) => (
              <div key={title} className="rounded-lg p-5 transition-colors group"
                style={{ background: C.card, border: `1px solid ${C.border}` }}
                onMouseEnter={e => e.currentTarget.style.borderColor = C.borderHover}
                onMouseLeave={e => e.currentTarget.style.borderColor = C.border}
                data-testid={`capability-card-${title.toLowerCase().replace(/\s+/g, "-")}`}>
                <Icon className="w-4 h-4 mb-3" style={{ color: C.accent }} />
                <h3 className="text-sm font-bold mb-1.5" style={{ color: C.text, fontFamily: "'JetBrains Mono', monospace" }}>{title}</h3>
                <p className="text-xs leading-relaxed" style={{ color: C.muted }}>{body}</p>
              </div>
            ))}

            {/* Coming Soon card */}
            <div
              className="rounded-lg p-5 flex flex-col justify-between"
              style={{ background: "transparent", border: `1px dashed ${C.border}` }}
              data-testid="capability-card-coming-soon">
              <div>
                <span className="inline-block text-[9px] font-bold uppercase tracking-[0.2em] px-2 py-0.5 rounded"
                  style={{ color: C.muted, border: `1px solid ${C.border}`, fontFamily: "'JetBrains Mono', monospace" }}>
                  Coming soon
                </span>
                <h3 className="text-sm font-bold mt-3 mb-1.5" style={{ color: "#555", fontFamily: "'JetBrains Mono', monospace" }}>
                  Alert Center (coming soon)
                </h3>
                <p className="text-xs leading-relaxed" style={{ color: "#555" }}>
                  Rules-based notifications flagged when your own regime, sojourn, and probability thresholds are met. MarkovLab never places, routes, or recommends orders — any action is taken by you, at your own broker.
                </p>
              </div>
            </div>
          </div>

          {/* Dashboard preview — frozen capture from a seeded sample account */}
          <div className="mt-8" data-testid="capabilities-dashboard-showcase">
            <DashboardShowcase C={C} />
          </div>
        </div>
      </section>
    </PublicPage>
  );
}
